import clsx from 'clsx';
import { cva, type VariantProps } from 'cva';
import { forwardRef, type ComponentPropsWithoutRef, type CSSProperties } from 'react';

export type CircleColor = 'transparent' | 'lightTransparent' | 'paper' | 'error' | 'default';

export type CirclePositionPreset = 'topLeft' | 'centerLeft' | 'centerRight' | 'bottomRight';

/** MUI-era alias — preset circles placed with legacy breakpoints (`min-[600px]` ≈ `sm`, `min-[900px]` ≈ `md`). */
export type ColorType = CircleColor;
export type PositionType = CirclePositionPreset;

const circleVariants = cva({
  base: 'absolute z-1 box-border rounded-full border-solid',
  variants: {
    color: {
      transparent: 'border-[rgba(255,255,255,0.75)]',
      lightTransparent: 'border-[rgba(239,247,255,0.2)]',
      paper: 'border-white',
      error: 'border-[#FA4969]',
      default: 'border-[#EFF7FF]',
    },
    preset: {
      explicitSize: '',
      topLeft: [
        'top-[-40px] left-[25px] h-20 w-20 border-16',
        'min-[600px]:top-[-150px] min-[600px]:h-[220px] min-[600px]:w-[220px] min-[600px]:border-40',
        'min-[900px]:top-[-250px] min-[900px]:left-[-15%] min-[900px]:h-[430px] min-[900px]:w-[430px] min-[900px]:border-80',
        'min-[1500px]:left-[5%]',
      ],
      bottomRight: [
        'right-[25px] bottom-[-40px] h-20 w-20 border-16',
        'min-[600px]:bottom-[-150px] min-[600px]:h-[220px] min-[600px]:w-[220px] min-[600px]:border-40',
        'min-[900px]:right-[-15%] min-[900px]:bottom-[-250px] min-[900px]:h-[430px] min-[900px]:w-[430px] min-[900px]:border-80',
        'min-[1500px]:right-[5%]',
      ],
      centerLeft: [
        'top-[40%] left-[-40px] h-20 w-20 max-w-none border-16',
        'min-[600px]:top-1/2 min-[600px]:left-[-140px] min-[600px]:h-[220px] min-[600px]:w-[220px] min-[600px]:-translate-y-1/2 min-[600px]:border-40',
        'min-[900px]:left-[-180px] min-[900px]:h-[340px] min-[900px]:w-[340px] min-[900px]:border-70',
      ],
      centerRight: [
        'top-[40%] right-[-40px] h-20 w-20 max-w-none border-16',
        'min-[600px]:top-1/2 min-[600px]:right-[-140px] min-[600px]:h-[220px] min-[600px]:w-[220px] min-[600px]:-translate-y-1/2 min-[600px]:border-40',
        'min-[900px]:right-[-180px] min-[900px]:h-[340px] min-[900px]:w-[340px] min-[900px]:border-70',
      ],
    },
  },
  defaultVariants: {
    color: 'transparent',
    preset: 'explicitSize',
  },
});

export type CircleVariantProps = VariantProps<typeof circleVariants>;

export type CircleProps = Omit<ComponentPropsWithoutRef<'div'>, 'color'> & {
  color?: CircleColor;
  /** Preset layout + responsive sizing (omit for explicit `size` / `border`). */
  position?: CirclePositionPreset;
  size?: number;
  border?: number;
};

const Circle = forwardRef<HTMLDivElement, CircleProps>(function Circle(
  { className, color = 'transparent', position, size = 580, border = 100, style, ...rest },
  ref,
) {
  const preset = position === undefined ? 'explicitSize' : position;

  const dimensionStyle: CSSProperties | undefined =
    preset === 'explicitSize' ? { width: size, height: size, borderWidth: border } : undefined;

  return (
    <div
      ref={ref}
      className={clsx(circleVariants({ color, preset }), className)}
      style={{ ...dimensionStyle, ...style }}
      {...rest}
    />
  );
});

export default Circle;
